/**
 * Portal home (tracker item 45, phase 3): balance due and a link to pay the
 * oldest unpaid invoice, the next visit, and the customer's properties each
 * with its next visit (the "grouped by property" view), and Request service.
 * An unlinked or pending login sees "We're matching your account", no customer
 * data, and can still request a service.
 */
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PortalInvoiceSummary, PortalJob, PortalMe } from '../../api/types';
import { usePortal, usePortalApi } from './PortalContext';
import { usePortalLoad } from './usePortalLoad';
import { EmptyBlock, FailureBlock, LoadingBlock } from './PortalStates';
import { RequestServiceEntry } from './PortalExtensionPoints';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import {
  localToday,
  nextVisit,
  oldestUnpaid,
  portalAddress,
  portalDay,
  portalWindow,
  propertiesWithNextVisit,
  totalBalanceDue,
} from '../../utils/portal';
import { primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';

const MAX_INVOICE_PAGES = 5;

/** The customer's invoices across pages (newest first), capped so a huge history stays cheap. */
export async function loadAllInvoices(api: ReturnType<typeof usePortalApi>): Promise<PortalInvoiceSummary[]> {
  const first = await api.listPortalInvoices({ page: 1, limit: 100 });
  const rows = [...first.data];
  for (let page = 2; page <= Math.min(first.pagination.pages, MAX_INVOICE_PAGES); page += 1) {
    rows.push(...(await api.listPortalInvoices({ page, limit: 100 })).data);
  }
  return rows;
}

function UnlinkedHome({ me }: { me: PortalMe }) {
  const declined = me.accessRequest?.status === 'DECLINED';
  return (
    <section aria-labelledby="matching-heading" className="rounded-lg bg-white p-5 shadow-sm ring-1 ring-gray-200" data-testid="unlinked-home">
      <h1 id="matching-heading" className="text-lg font-bold text-gray-900">
        {declined ? "We couldn't match your account" : "We're matching your account"}
      </h1>
      {declined ? (
        <p className="mt-2 text-sm text-gray-700">
          We were not able to connect your login to a customer account. Please contact {me.companyName} and we will sort it out.
        </p>
      ) : (
        <p className="mt-2 text-sm text-gray-700">
          Your email is confirmed. {me.companyName} is matching your login to your customer account. Once that is done you will see your
          invoices, visits and payment options here. There is nothing more you need to do; this can take a business day.
        </p>
      )}
      <p className="mt-2 text-xs text-gray-500">Signed in as {me.user.email}</p>
      <RequestServiceEntry me={me} />
    </section>
  );
}

function LinkedHome({ me }: { me: PortalMe }) {
  const api = usePortalApi();
  const today = localToday();
  const load = useCallback(async () => {
    const [invoices, jobs] = await Promise.all([loadAllInvoices(api), api.listPortalJobs({ page: 1, limit: 100 })]);
    return { invoices, jobs: jobs.data };
  }, [api]);
  const { data, loading, failure, reload } = usePortalLoad(load, 'We could not load your account.');

  if (loading && !data) return <LoadingBlock label="Loading your account" />;
  if (failure) return <FailureBlock failure={failure} onRetry={() => void reload()} />;
  if (!data) return null;

  const { invoices, jobs } = data as { invoices: PortalInvoiceSummary[]; jobs: PortalJob[] };
  const balance = totalBalanceDue(invoices);
  const oldest = oldestUnpaid(invoices);
  const next = nextVisit(jobs, today);
  const sites = propertiesWithNextVisit(me, jobs, today);

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold text-gray-900">Hello{me.user.firstName ? `, ${me.user.firstName}` : ''}</h1>

      <section aria-labelledby="balance-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <h2 id="balance-heading" className="text-sm font-semibold text-gray-600">Balance due</h2>
        <p className="mt-1 text-3xl font-bold text-gray-900" data-testid="balance-due">{invoiceMoney(balance)}</p>
        {oldest ? (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <Link to={`/portal/invoices/${oldest.id}?pay=1`} className={`${primaryButtonClass} inline-flex items-center justify-center text-base`}>
              Pay invoice {oldest.invoiceNumber} ({invoiceMoney(oldest.balanceDue)})
            </Link>
            <Link to="/portal/invoices" className={`${secondaryButtonClass} inline-flex items-center justify-center`}>All invoices</Link>
          </div>
        ) : (
          <p className="mt-2 text-sm text-gray-600">You are all paid up. Thank you!</p>
        )}
        {oldest && <p className="mt-2 text-xs text-gray-500">Oldest unpaid: invoice {oldest.invoiceNumber}, due {invoiceDate(oldest.dueDate)}.</p>}
      </section>

      <section aria-labelledby="next-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <h2 id="next-heading" className="text-sm font-semibold text-gray-600">Next visit</h2>
        {next ? (
          <Link to={`/portal/jobs/${next.id}`} className="mt-1 block min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="next-visit">
            <span className="block text-base font-semibold text-gray-900">{portalDay(next.scheduledDate)}</span>
            <span className="block text-sm text-gray-700">{portalWindow(next)}</span>
            <span className="block text-sm text-gray-600">{portalAddress(next.property)}</span>
          </Link>
        ) : (
          <p className="mt-1 text-sm text-gray-600">No visits are scheduled right now.</p>
        )}
      </section>

      <RequestServiceEntry me={me} />

      <section aria-labelledby="properties-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <h2 id="properties-heading" className="text-sm font-semibold text-gray-600">Your properties</h2>
        {sites.length === 0 ? (
          <EmptyBlock>No properties are on your account yet.</EmptyBlock>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100" aria-label="Properties">
            {sites.map(({ property, next: visit }) => (
              <li key={property.id} className="py-3" data-testid="property-row">
                <p className="text-sm font-semibold text-gray-900">{portalAddress(property)}</p>
                {property.relation === 'BILLED_TO_YOU' && <p className="text-xs text-gray-500">Billed to you</p>}
                <p className="text-xs text-gray-600">
                  {property.relation === 'BILLED_TO_YOU' ? 'Visit details are with the property owner.' : visit ? `Next visit ${portalDay(visit.scheduledDate)}` : 'No visit scheduled'}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <nav aria-label="More" className="flex flex-wrap gap-x-2">
        <Link to="/portal/payment-methods" className="inline-flex min-h-[44px] items-center px-1 text-sm font-semibold text-blue-700 hover:underline">Payment methods</Link>
        <Link to="/portal/statement" className="inline-flex min-h-[44px] items-center px-1 text-sm font-semibold text-blue-700 hover:underline">Statement</Link>
      </nav>
    </div>
  );
}

export function PortalDashboardPage() {
  const { me } = usePortal();
  return me.status === 'LINKED' ? <LinkedHome me={me} /> : <UnlinkedHome me={me} />;
}
